import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BottlingStatusBadge } from "@/components/bottling/BottlingStatusBadge";
import { SkuModal, SubmitModal, EditWarningModal } from "@/components/bottling/BottlingModals";
import {
  useBottlingClientEvents,
  useBottlingEvent,
  useBottlingSkus,
  useBottlingPastEvents,
  useSubmitBottlingEvent,
  useSaveBottlingDraft,
  useUpdateBottlingSkus,
  useLogBottlingRevision,
} from "@/hooks/useBottlingEvents";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import {
  fmtDate,
  fmtDateShort,
  calcLockDate,
  calcGallons,
  PAGE_SIZE,
  BOTTLE_TYPES,
  BOTTLE_FORMATS,
  BLANK_SKU,
  type BottlingSku,
  type BottlingEventStatus,
} from "@/lib/bottling-constants";

// ─── Search helper ────────────────────────────────────────────────────────

function searchEvents(events: any[], query: string) {
  if (!query) return events;
  const q = query.toLowerCase();
  return events.filter((e: any) =>
    e.name?.toLowerCase().includes(q) ||
    e.status?.toLowerCase().includes(q) ||
    e.confirmed_date?.includes(q) ||
    e.preferred_date_from?.includes(q)
  );
}

// ─── Main Router ──────────────────────────────────────────────────────────

export default function BottlingClient() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view");
  const eventId = searchParams.get("event");

  if (view === "new") return <NewEventPage />;
  if (eventId) return <ClientEventDetail eventId={eventId} />;
  return <ClientDashboard />;
}

// ─── Client Dashboard ─────────────────────────────────────────────────────

function ClientDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: events = [], isLoading } = useBottlingClientEvents(user?.id);
  const { data: pastEvents = [] } = useBottlingPastEvents(user?.id);
  const [query, setQuery] = useState("");
  const [showPast, setShowPast] = useState(false);

  const active = events.filter((e: any) => e.status !== "completed");
  const searched = searchEvents(active, query);
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(searched.length / PAGE_SIZE);
  const pageItems = searched.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mx-auto max-w-5xl px-8 py-10">
      <div className="mb-8 flex items-end justify-between border-b pb-5">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">My Bottlings</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Track your bottling requests and events</p>
        </div>
        <Button onClick={() => navigate("/client/bottling?view=new")}>+ New Bottling Request</Button>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search events…"
          onChange={e => { setQuery(e.target.value); setPage(1); }}
          className="w-64"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Events</CardTitle>
          <span className="text-[11px] text-muted-foreground">{active.length} event{active.length !== 1 ? "s" : ""}</span>
        </CardHeader>
        {isLoading ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : pageItems.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            {query ? `No events match "${query}"` : "No active events."}
            {!query && <Button variant="ghost" size="sm" className="ml-2" onClick={() => navigate("/client/bottling?view=new")}>Create your first →</Button>}
          </div>
        ) : (
          <>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {["Bottling Window", "Confirmed Date", "Est. Cases", "Status", ""].map((h, i) => (
                    <th key={i} className="border-b bg-muted px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageItems.map((evt: any) => (
                  <tr
                    key={evt.id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/client/bottling?event=${evt.id}`)}
                  >
                    <td className="px-4 py-3.5">
                      <div className="font-medium">{evt.name || fmtDate(evt.preferred_date_from)}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {[evt.preferred_date_from, evt.preferred_date_to].filter(Boolean).map(fmtDateShort).join(" – ")}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">{evt.confirmed_date ? fmtDateShort(evt.confirmed_date) : <span className="text-muted-foreground">Pending</span>}</td>
                    <td className="px-4 py-3.5 font-mono">{evt.est_cases?.toLocaleString() || "—"}</td>
                    <td className="px-4 py-3.5"><BottlingStatusBadge status={evt.status} /></td>
                    <td className="px-4 py-3.5 text-right"><Button variant="ghost" size="sm">View →</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-muted-foreground">
                <span>{searched.length} total</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                  <span className="px-2 py-1">Page {page} of {totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Past events */}
      <div className="mt-10">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Past Events</span>
          <Button variant="ghost" size="sm" onClick={() => setShowPast(v => !v)}>{showPast ? "Hide" : "Show"}</Button>
        </div>
        {showPast && (
          <Card>
            {pastEvents.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">No past events yet.</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {["Preferred Window", "Bottling Date", "Status", "Cases"].map((h, i) => (
                      <th key={i} className="border-b bg-muted px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pastEvents.map((e: any) => (
                    <tr key={e.id} className="border-b last:border-0">
                      <td className="px-4 py-3">{e.preferred_date_from ? fmtDateShort(e.preferred_date_from) : "—"}</td>
                      <td className="px-4 py-3">{e.confirmed_date ? fmtDateShort(e.confirmed_date) : "—"}</td>
                      <td className="px-4 py-3"><BottlingStatusBadge status={e.status} /></td>
                      <td className="px-4 py-3 font-mono">{e.est_cases || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── New Event Page ───────────────────────────────────────────────────────

function NewEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  const submitEvent = useSubmitBottlingEvent();
  const saveDraft = useSaveBottlingDraft();

  const [skus, setSkus] = useState<BottlingSku[]>([{ ...BLANK_SKU }]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [facility, setFacility] = useState("Gravity Wine House");
  const [notes, setNotes] = useState("");
  const [showSkuModal, setShowSkuModal] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);

  const totalCases = skus.reduce((s, x) => s + Number(x.cases || 0), 0);
  const totalGallons = skus.reduce((s, x) => s + calcGallons(Number(x.cases || 0), Number(x.casePack || 12), x.format || "750ml"), 0);

  function updateSku(idx: number, field: keyof BottlingSku, val: any) {
    setSkus(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  }

  async function handleSubmit() {
    if (!user || !profile?.org_id) { alert("Error: No organization found."); return; }
    if (!dateFrom) { alert("Please enter a preferred start date."); return; }
    const autoName = new Date(dateFrom + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    await submitEvent.mutateAsync({
      userId: user.id,
      orgId: profile.org_id,
      formData: { name: autoName, facility, dateFrom, dateTo, clientNotes: notes },
      skus,
    });
    setShowSubmit(false);
    navigate("/client/bottling");
  }

  async function handleSaveDraft() {
    if (!user || !profile?.org_id) { alert("Error: No organization found."); return; }
    const autoName = dateFrom ? new Date(dateFrom + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "Draft";
    await saveDraft.mutateAsync({
      userId: user.id,
      orgId: profile.org_id,
      formData: { name: autoName, facility, dateFrom, dateTo, clientNotes: notes },
      skus,
    });
  }

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <div className="mb-8 flex items-end justify-between border-b pb-5">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">New Bottling Request</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Add your wines, then submit your preferred scheduling window</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleSaveDraft} disabled={submitEvent.isPending || saveDraft.isPending}>Save Draft</Button>
          <Button onClick={() => setShowSubmit(true)} disabled={submitEvent.isPending || skus.length === 0}>Review & Submit →</Button>
        </div>
      </div>

      {/* Scheduling request */}
      <Card className="mb-6">
        <CardHeader><CardTitle>Scheduling Request</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Preferred Start Date *</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Preferred End Date</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Facility</Label>
            <Select value={facility} onValueChange={setFacility}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Gravity Wine House">Gravity Wine House</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Notes for GWH</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any scheduling notes…" />
          </div>
        </CardContent>
      </Card>

      {/* SKU sheet */}
      <Card>
        <CardHeader>
          <CardTitle>Wines / SKUs</CardTitle>
          <div className="flex items-center gap-3">
            <span className="rounded-full border px-3 py-1 text-[11px] text-muted-foreground">
              {skus.length} SKU{skus.length !== 1 ? "s" : ""} · {totalCases.toLocaleString()} cases · {totalGallons.toLocaleString()} gal
            </span>
            <Button size="sm" onClick={() => setSkus(p => [...p, { ...BLANK_SKU }])}>+ Add Row</Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSkuModal(true)}>+ Form</Button>
          </div>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px] border-collapse text-sm">
            <thead>
              <tr>
                {["", "Brand", "Wine", "Vintage", "Bottle", "Format", "Closure", "Label", "Capsule", "Pack", "Cases", "Gallons", "Fanciful", "AVA", "Stickers", "Filter", "DMDC", "Lot Code", "Notes", ""].map((h, i) => (
                  <th key={i} className="border-b bg-muted px-3 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skus.map((sku, idx) => (
                <SkuRow key={idx} sku={sku} onChange={(f, v) => updateSku(idx, f, v)} onRemove={() => setSkus(p => p.filter((_, i) => i !== idx))} />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={10} className="border-t-2 bg-muted px-3 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Total</td>
                <td className="border-t-2 bg-muted px-3 py-2.5 font-mono font-semibold">{totalCases.toLocaleString()}</td>
                <td className="border-t-2 bg-muted px-3 py-2.5 font-mono text-muted-foreground">{totalGallons.toLocaleString()} gal</td>
                <td colSpan={8} className="border-t-2 bg-muted"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      <SkuModal open={showSkuModal} onClose={() => setShowSkuModal(false)} onAdd={s => setSkus(p => [...p, s])} />
      <SubmitModal open={showSubmit} onClose={() => setShowSubmit(false)} onConfirm={handleSubmit} dateFrom={dateFrom} dateTo={dateTo} skuCount={skus.length} totalCases={totalCases} />
    </div>
  );
}

// ─── SKU Row ──────────────────────────────────────────────────────────────

function SkuRow({ sku, onChange, onRemove }: { sku: BottlingSku; onChange: (f: keyof BottlingSku, v: any) => void; onRemove: () => void }) {
  const gals = calcGallons(Number(sku.cases || 0), Number(sku.casePack || 12), sku.format || "750ml");
  const inp = "rounded border border-transparent bg-transparent px-2 py-1.5 text-sm hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/10";

  return (
    <tr className="border-b hover:bg-muted/30">
      <td className="cursor-grab px-2 py-1 text-center text-muted-foreground">⠿</td>
      <td className="p-1"><input className={`w-full ${inp}`} value={sku.brand} onChange={e => onChange("brand", e.target.value)} placeholder="Brand…" /></td>
      <td className="p-1"><input className={`w-full ${inp}`} value={sku.wine} onChange={e => onChange("wine", e.target.value)} placeholder="Wine…" /></td>
      <td className="p-1"><input className={`w-16 ${inp}`} value={sku.vintage} onChange={e => onChange("vintage", e.target.value)} placeholder="2024" /></td>
      <td className="p-1">
        <input className={`w-24 ${inp}`} list="row-bottles" value={sku.bottle} onChange={e => onChange("bottle", e.target.value)} placeholder="Burgundy" />
        <datalist id="row-bottles">{BOTTLE_TYPES.map(b => <option key={b}>{b}</option>)}</datalist>
      </td>
      <td className="p-1">
        <select className={inp} value={sku.format} onChange={e => onChange("format", e.target.value)}>
          {BOTTLE_FORMATS.map(f => <option key={f}>{f}</option>)}
        </select>
      </td>
      <td className="p-1">
        <select className={inp} value={sku.closure} onChange={e => onChange("closure", e.target.value)}>
          <option value="">—</option><option>Cork</option><option>Screw Cap</option>
        </select>
      </td>
      <td className="p-1">
        <select className={inp} value={sku.label} onChange={e => onChange("label", e.target.value)}>
          <option>Yes</option><option>No</option>
        </select>
      </td>
      <td className="p-1">
        <select className={inp} value={sku.capsule} onChange={e => onChange("capsule", e.target.value)}>
          <option>No</option><option>Yes</option>
        </select>
      </td>
      <td className="p-1">
        <input type="number" className={`w-14 text-right font-mono ${inp}`} value={sku.casePack} onChange={e => onChange("casePack", +e.target.value)} />
      </td>
      <td className="p-1">
        <input type="number" className={`w-16 text-right font-mono ${inp}`} value={sku.cases} onChange={e => onChange("cases", +e.target.value)} />
      </td>
      <td className="px-3 py-2 text-right font-mono text-[12px] text-muted-foreground">{gals > 0 ? gals.toLocaleString() : "—"}</td>
      <td className="p-1"><input className={`w-28 ${inp}`} value={sku.fanciful || ""} onChange={e => onChange("fanciful", e.target.value)} placeholder="Fanciful…" /></td>
      <td className="p-1"><input className={`w-28 ${inp}`} value={sku.ava || ""} onChange={e => onChange("ava", e.target.value)} placeholder="AVA…" /></td>
      <td className="p-1 text-center">
        <input type="checkbox" className="h-4 w-4 rounded border-border accent-primary" checked={!!sku.caseStickers} onChange={e => onChange("caseStickers", e.target.checked)} />
      </td>
      <td className="p-1">
        <select className={inp} value={sku.filter} onChange={e => onChange("filter", e.target.value)}>
          <option value="">—</option><option value="10µ/BC">10µ/BC</option><option value="Sterile">Sterile</option>
        </select>
      </td>
      <td className="p-1">
        <select className={inp} value={sku.dmdc} onChange={e => onChange("dmdc", e.target.value)}>
          <option>No</option><option>Yes</option>
        </select>
      </td>
      <td className="p-1"><input className={`w-24 font-mono ${inp}`} value={sku.lotCode} onChange={e => onChange("lotCode", e.target.value)} placeholder="Lot…" /></td>
      <td className="p-1"><input className={`w-28 ${inp}`} value={sku.instructions} onChange={e => onChange("instructions", e.target.value)} placeholder="Notes…" /></td>
      <td className="p-1"><button className="rounded px-2 py-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" onClick={onRemove}>×</button></td>
    </tr>
  );
}

// ─── Client Event Detail ──────────────────────────────────────────────────

function ClientEventDetail({ eventId }: { eventId: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  const { data: evt, isLoading } = useBottlingEvent(eventId);
  const { data: rawSkus = [] } = useBottlingSkus(eventId);
  const updateSkus = useUpdateBottlingSkus();
  const logRevision = useLogBottlingRevision();

  const [editMode, setEditMode] = useState(false);
  const [showEditWarn, setShowEditWarn] = useState(false);

  // Convert DB shape to BottlingSku shape for editing
  const [skus, setSkus] = useState<BottlingSku[]>([]);
  const [initialized, setInitialized] = useState(false);
  if (rawSkus.length > 0 && !initialized) {
    setSkus(rawSkus.map((s: any) => ({
      id: s.id,
      brand: s.brand || "",
      wine: s.wine || "",
      vintage: s.vintage || "",
      bottle: s.bottle_type || "",
      format: s.bottle_format || "750ml",
      closure: s.closure || "",
      capsule: s.capsule ? "Yes" : "No",
      casePack: s.case_pack || 12,
      cases: s.est_cases || 0,
      filter: s.filter_type || "",
      dmdc: s.dmdc ? "Yes" : "No",
      label: s.label || "Yes",
      fanciful: s.fanciful_name || "",
      ava: s.ava || "",
      lotCode: s.lot_code || "",
      instructions: s.instructions || "",
    })));
    setInitialized(true);
  }

  const locked = evt?.status === "locked";

  async function handleSave() {
    if (!user) return;
    await updateSkus.mutateAsync({ eventId, skus, orgId: profile?.org_id });
    await logRevision.mutateAsync({ eventId, changedBy: user.id, changeType: "sku_update", summary: "SKUs updated by client." });
    setEditMode(false);
  }

  if (isLoading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (!evt) return <div className="p-10 text-center text-muted-foreground">Event not found.</div>;

  const titleDate = evt.confirmed_date || evt.preferred_date_from;
  const titleLabel = titleDate ? "Bottling · " + fmtDate(titleDate) : "Bottling Request";
  const totalCases = skus.reduce((s, x) => s + Number(x.cases || 0), 0);
  const status = evt.status as BottlingEventStatus;

  return (
    <div className="mx-auto max-w-5xl px-8 py-10">
      <button
        className="mb-6 text-[11px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => navigate("/client/bottling")}
      >
        ← Back to My Bottlings
      </button>

      <div className="mb-8 flex items-end justify-between border-b pb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight">{titleLabel}</h1>
            <BottlingStatusBadge status={status} />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {evt.confirmed_date ? fmtDate(evt.confirmed_date) : "Awaiting confirmation"}
            {evt.confirmed_date && evt.lock_date && <span> · Locks <strong className="text-foreground">{fmtDate(evt.lock_date)}</strong></span>}
          </p>
        </div>
        {!locked && (
          editMode ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setShowEditWarn(true)}>Edit</Button>
          )
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Wines / SKUs</CardTitle>
          <span className="text-[12px] text-muted-foreground">
            {locked ? "Locked — contact GWH to make changes" : `${skus.length} SKU${skus.length !== 1 ? "s" : ""} · ${totalCases.toLocaleString()} cases`}
          </span>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr>
                {["#", "Brand", "Wine", "Vintage", "Bottle", "Format", "Closure", "Label", "Capsule", "Pack", "Cases", "Filter", "DMDC", "Lot Code", "Instructions"].map((h, i) => (
                  <th key={i} className="border-b bg-muted px-3 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skus.map((s, i) => (
                <tr key={s.id || i} className="border-b last:border-0">
                  <td className="px-3 py-3 text-[11px] text-muted-foreground">{i + 1}</td>
                  {editMode ? (
                    <>
                      {(["brand", "wine", "vintage", "bottle"] as const).map(f => (
                        <td key={f} className="p-1">
                          <input className="w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm hover:border-border focus:border-primary focus:outline-none"
                            value={s[f]} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, [f]: e.target.value } : x))} />
                        </td>
                      ))}
                      <td className="p-1">
                        <select className="rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.format} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, format: e.target.value } : x))}>
                          {BOTTLE_FORMATS.map(f => <option key={f}>{f}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <select className="rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.closure} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, closure: e.target.value } : x))}>
                          <option>Cork</option><option>Screw Cap</option>
                        </select>
                      </td>
                      <td className="p-1"><select className="rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.label} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}><option>Yes</option><option>No</option></select></td>
                      <td className="p-1"><select className="rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.capsule} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, capsule: e.target.value } : x))}><option>No</option><option>Yes</option></select></td>
                      <td className="p-1"><input type="number" className="w-14 rounded border border-border bg-background px-2 py-1.5 text-right text-sm font-mono" value={s.casePack} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, casePack: +e.target.value } : x))} /></td>
                      <td className="p-1"><input type="number" className="w-16 rounded border border-border bg-background px-2 py-1.5 text-right text-sm font-mono" value={s.cases} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, cases: +e.target.value } : x))} /></td>
                      <td className="p-1"><select className="rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.filter} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, filter: e.target.value } : x))}><option value="">—</option><option>10µ/BC</option><option>Sterile</option></select></td>
                      <td className="p-1"><select className="rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.dmdc} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, dmdc: e.target.value } : x))}><option>No</option><option>Yes</option></select></td>
                      <td className="p-1"><input className="w-24 rounded border border-border bg-background px-2 py-1.5 text-sm font-mono" value={s.lotCode} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, lotCode: e.target.value } : x))} /></td>
                      <td className="p-1"><input className="w-28 rounded border border-border bg-background px-2 py-1.5 text-sm" value={s.instructions} onChange={e => setSkus(p => p.map((x, j) => j === i ? { ...x, instructions: e.target.value } : x))} /></td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-3">{s.brand}</td>
                      <td className="px-3 py-3 font-medium">{s.wine}</td>
                      <td className="px-3 py-3">{s.vintage}</td>
                      <td className="px-3 py-3">{s.bottle}</td>
                      <td className="px-3 py-3">{s.format || "750ml"}</td>
                      <td className="px-3 py-3">{s.closure}</td>
                      <td className="px-3 py-3">{s.label || "Yes"}</td>
                      <td className="px-3 py-3">{s.capsule || "No"}</td>
                      <td className="px-3 py-3 text-right font-mono">{s.casePack}</td>
                      <td className="px-3 py-3 text-right font-mono font-semibold">{s.cases}</td>
                      <td className="px-3 py-3">{s.filter || "—"}</td>
                      <td className="px-3 py-3">{s.dmdc || "No"}</td>
                      <td className="px-3 py-3 font-mono text-xs">{s.lotCode || "—"}</td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{s.instructions || "—"}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <EditWarningModal
        open={showEditWarn}
        onClose={() => setShowEditWarn(false)}
        onConfirm={() => { setShowEditWarn(false); setEditMode(true); }}
        confirmedDate={evt.confirmed_date}
      />
    </div>
  );
}
